import { useEffect } from "react";

/** Typing goes to the field it is in: no shortcut fires there. */
function isTyping(target: EventTarget | null) {
  const element = target as HTMLElement | null;
  return Boolean(element && (element.isContentEditable || element.closest("input, textarea, select, [contenteditable]")));
}

/** A linked row's way in: the link marked `data-row-link`, else its first. */
function rowLink(row: Element) {
  return row.querySelector<HTMLAnchorElement>("a[data-row-link]") ?? row.querySelector<HTMLAnchorElement>("a[href]");
}

/**
 * The keyboard of a list page, for people who work through a list all day: `/` puts the cursor in the page's search
 * (`SearchInput`, or any field with `aria-keyshortcuts="/"`); `j` and `k` move to the next and previous record of the
 * page's linked rows (`TR linked`), focusing the row's link so Enter opens it as a click would. Nothing fires while a
 * person types in a field, holds a modifier or has a dialog open. Mounted once by the shell; a page adds nothing.
 */
export function useListKeys() {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return;
      if (document.querySelector("dialog[open]")) return;
      if (event.key === "/") {
        const search = document.querySelector<HTMLInputElement>('main [aria-keyshortcuts="/"]');
        if (!search) return;
        event.preventDefault();
        search.focus();
        search.select();
        return;
      }
      if (event.key !== "j" && event.key !== "k") return;
      const rows = [...document.querySelectorAll("main tr[data-linked]")].filter((row) => rowLink(row));
      if (rows.length === 0) return;
      event.preventDefault();
      const current = rows.findIndex((row) => row.contains(document.activeElement));
      const next = current === -1 ? 0 : Math.min(rows.length - 1, Math.max(0, current + (event.key === "j" ? 1 : -1)));
      rowLink(rows[next])!.focus({ preventScroll: true });
      rows[next].scrollIntoView({ block: "nearest" });
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}
